import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useMatchXSBreakpoint } from '../../hooks';
import Paragraph from '../paragraph';
import IconButton from '../icon-button';
import ActionButton from '../action-button';
import Icon from '../icon';
import { iconsList } from '../icon/glyphs';
import { APPEARANCES, ALIGN } from './constants';
import getAppearanceConfig from './helpers/get-appearance-config';

const NotificationMessage = ({
  title,
  description = '',
  iconGlyph = null,
  appearance = APPEARANCES.default,
  align = ALIGN.left,
  actionButtonLabel = '',
  isActionButtonLoading = false,
  onActionButtonClick = null,
  onClose = null,
}) => {
  const { iconType, actionButtonAppearance, titleAppearance } = getAppearanceConfig(appearance);

  const isLayoutMobile = !useMatchXSBreakpoint();
  const hasCloseButton = Boolean(onClose);
  const hasActionButton = Boolean(actionButtonLabel && onActionButtonClick);

  const notificationMessageClassName = classNames(
    'ws-ui-notification-message',
    `ws-ui-notification-message--${appearance}`,
    `ws-ui-notification-message--${align}`,
    { 'ws-ui-notification-message--layout-mobile': isLayoutMobile },
    { 'ws-ui-notification-message--with-icon': iconGlyph },
    { 'ws-ui-notification-message--with-close-button': hasCloseButton },
    { 'ws-ui-notification-message--with-action-button': hasActionButton }
  );

  const titleElement =
    typeof title === 'string' ? <Paragraph appearance={titleAppearance}>{title}</Paragraph> : title;
  const descriptionElement =
    typeof description === 'string' ? (
      <Paragraph size="small" appearance="medium-emphasis">
        {description}
      </Paragraph>
    ) : (
      description
    );

  return (
    <div className={notificationMessageClassName}>
      <div className="ws-ui-notification-message__content">
        {iconGlyph && (
          <div className="ws-ui-notification-message__icon">
            <Icon glyph={iconGlyph} type={iconType} size="100%" />
          </div>
        )}
        <div className="ws-ui-notification-message__text">
          {titleElement}
          {description && descriptionElement}
        </div>
        {hasActionButton && (
          <div className="ws-ui-notification-message__action-button">
            <ActionButton
              appearance={actionButtonAppearance}
              format="solid"
              size="small"
              label={actionButtonLabel}
              fullWidth={isLayoutMobile}
              onClick={onActionButtonClick}
              isLoading={isActionButtonLoading}
            />
          </div>
        )}
      </div>
      {hasCloseButton && (
        <div className="ws-ui-notification-message__close-button">
          <IconButton onClick={onClose} size="medium" glyph="close" transparent />
        </div>
      )}
    </div>
  );
};

NotificationMessage.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  iconGlyph: PropTypes.oneOf(iconsList),
  appearance: PropTypes.oneOf(Object.values(APPEARANCES)),
  align: PropTypes.oneOf(Object.values(ALIGN)),
  actionButtonLabel: PropTypes.string,
  isActionButtonLoading: PropTypes.bool,
  onActionButtonClick: PropTypes.func,
  onClose: PropTypes.func,
};

export default NotificationMessage;
